import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Box, CircularProgress, Typography, Button, Paper, IconButton } from '@mui/material';
import { CloudUpload, ArrowBack } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { DocumentViewer, CommentData } from '@components/DocumentViewer/DocumentViewer';
import { mockSurveyService } from '@services/mocksurvey';
import { toast } from 'react-toastify';
import { auditLogService } from '@services/auditLog';
import { getCurrentUserId } from '@utils/userUtils';
import { getSelectedFacilityFromSession } from '@utils/facilityUtils';
import {
  canAddByName,
  canUpdateMockSurveyFromPermissions,
  FEATURE_NAMES,
  normalizePermissionList,
  type Permission,
} from '@utils/permissions';
import { fetchUserPermissions } from '@services/UserPermission';

function commentAuthorFromUser(stored?: string, userid?: number): string {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    if (
      name &&
      ((userid && String(userid) === String(user.id ?? user.user_id)) ||
        (stored &&
          user.email &&
          stored.toLowerCase() === String(user.email).toLowerCase()))
    ) {
      return name;
    }
  } catch {
    /* ignore */
  }
  return stored || 'Unknown';
}

const SurveyViewer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [docxBlob, setDocxBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false); // Separate state for download button loading
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [fileId, setFileId] = useState<number | string | undefined>(undefined);
  const [comments, setComments] = useState<CommentData[]>([]);
  const [facilityPermissions, setFacilityPermissions] = useState<Permission[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canUpdateMockSurvey = useMemo(
    () => canUpdateMockSurveyFromPermissions(facilityPermissions),
    [facilityPermissions],
  );
  const viewerReadOnly = !canUpdateMockSurvey;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const facility = getSelectedFacilityFromSession();
      const userId = getCurrentUserId();
      if (!facility?.facility_id || !userId || userId === '0') {
        return;
      }
      try {
        const permissionsResponse = await fetchUserPermissions(
          userId,
          facility.facility_id,
        );
        const permissions =
          (permissionsResponse as any)?.result?.data ||
          permissionsResponse?.permissions ||
          (permissionsResponse as any)?.items ||
          (permissionsResponse as any)?.results ||
          [];
        if (!cancelled) {
          setFacilityPermissions(normalizePermissionList(permissions as unknown[]));
        }
      } catch (error) {
        console.error('Failed to load facility permissions for survey viewer:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const logSurveyViewerAuditEvent = useCallback(
    async (
      actionType: 'VIEW' | 'UPLOAD' | 'DOWNLOAD' | 'CREATE' | 'UPDATE' | 'DELETE',
      endpoint: string,
      method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
      actionDetails: Record<string, any> = {},
      resourceType: 'MOCK_SURVEY' | 'SURVEY_VIEWER' = 'SURVEY_VIEWER',
      statusCode = 200,
      errorMessage: string | null = null,
    ) => {
      const userId = parseInt(getCurrentUserId(), 10);
      if (!Number.isFinite(userId)) return;
      const facility = getSelectedFacilityFromSession();
      await auditLogService.logApiCallToAuditLog({
        user_id: userId,
        endpoint,
        method,
        request_payload: {},
        response_payload: {},
        status_code: statusCode,
        error_message: errorMessage,
        facility_info: facility ? {
          facility_id: facility.facility_id,
          facility_name: facility.facility_name,
        } : {},
        action_details: {
          page: resourceType === 'MOCK_SURVEY' ? 'Mock Survey' : 'Survey Viewer from Mock survey screen',
          fileId,
          fileName,
          ...actionDetails,
        },
        resource_type: resourceType,
        action_type: actionType,
        action_status: errorMessage ? 'FAILURE' : 'SUCCESS',
      });
    },
    [fileId, fileName],
  );

  // Log page access to audit log
  useEffect(() => {
    void logSurveyViewerAuditEvent('VIEW', '/dashboard/survey-viewer', 'GET', {
      action: 'VIEW',
      resource: 'SURVEY_VIEWER',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load file from navigation state, sessionStorage, or localStorage (for persistence across refreshes)
  useEffect(() => {
    const loadFileFromState = async () => {
      try {
        const state = location.state as any;
        
        // Priority 1: Check location.state first (for immediate navigation with fileBlob)
        if (state?.fileBlob) {
          setDocxBlob(state.fileBlob);
          setFileName(state.fileName || 'document.docx');
          if (state.fileId !== undefined) {
            setFileId(state.fileId);
            // Store fileId in fileInfo for persistence
            const fileInfo = JSON.stringify({
              fileName: state.fileName || 'document.docx',
              fileType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              fileId: state.fileId,
            });
            sessionStorage.setItem('surveyViewerFile', fileInfo);
            // Convert blob to base64 for storage
            const arrayBuffer = await state.fileBlob.arrayBuffer();
            const bytes = new Uint8Array(arrayBuffer);
            let binary = '';
            for (let i = 0; i < bytes.length; i++) {
              binary += String.fromCharCode(bytes[i]);
            }
            const base64Data = btoa(binary);
            sessionStorage.setItem('surveyViewerFileData', base64Data);
            localStorage.setItem('surveyViewerFile', fileInfo);
            localStorage.setItem('surveyViewerFileData', base64Data);
          }
          return;
        }
        
        // Priority 2: Check sessionStorage (for new navigation)
        let fileInfoStr = sessionStorage.getItem('surveyViewerFile');
        let fileDataStr = sessionStorage.getItem('surveyViewerFileData');
        
        // Priority 3: If not in sessionStorage, check localStorage (for persistence after refresh)
        if (!fileInfoStr || !fileDataStr) {
          fileInfoStr = localStorage.getItem('surveyViewerFile');
          fileDataStr = localStorage.getItem('surveyViewerFileData');
        }
        
        if (fileInfoStr && fileDataStr) {
          const fileInfo = JSON.parse(fileInfoStr);
          const base64Data = fileDataStr;
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: fileInfo.fileType || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
          setDocxBlob(blob);
          setFileName(fileInfo.fileName || 'document.docx');
          
          // Set fileId from fileInfo or location.state
          if (fileInfo.fileId !== undefined) {
            setFileId(fileInfo.fileId);
          } else if (state?.fileId !== undefined) {
            setFileId(state.fileId);
            // Update fileInfo to include fileId
            fileInfo.fileId = state.fileId;
            const updatedFileInfo = JSON.stringify(fileInfo);
            if (sessionStorage.getItem('surveyViewerFile')) {
              sessionStorage.setItem('surveyViewerFile', updatedFileInfo);
            }
            if (localStorage.getItem('surveyViewerFile')) {
              localStorage.setItem('surveyViewerFile', updatedFileInfo);
            }
          }
          
          // Move from sessionStorage to localStorage for persistence
          if (sessionStorage.getItem('surveyViewerFile')) {
            localStorage.setItem('surveyViewerFile', fileInfoStr);
            localStorage.setItem('surveyViewerFileData', fileDataStr);
            sessionStorage.removeItem('surveyViewerFile');
            sessionStorage.removeItem('surveyViewerFileData');
          }
          return;
        }

        // Fallback: Check location state (for fileName and fileId only, no blob)
        if (state?.fileName) {
          setFileName(state.fileName);
        }
        if (state?.fileId !== undefined) {
          setFileId(state.fileId);
        }
      } catch (error) {
        console.error('Failed to load file from state', error);
        setError('Failed to load document. Please try again.');
      }
    };

    loadFileFromState();
  }, [location.state]);

  // Load comments from API - extracted as a reusable function
  const loadCommentsFromAPI = useCallback(async () => {
    if (!fileId || !docxBlob) {
      setComments([]);
      return;
    }

    try {
      console.log('📥 Loading comments from API for fileId:', fileId);
      const response = await mockSurveyService.getComments(fileId);
      console.log('📥 Loaded comments from API:', response);

      // Convert API response to CommentData format
      const convertedComments: CommentData[] = response.items.map((item, index) => {
        const commentJson = item.commentsjson;
        const paragraphText = commentJson.paragraphText || '';
        const startOffset = commentJson.startOffset || 0;
        const endOffset = commentJson.endOffset || 0;
        
        return {
          id: `api-comment-${item.id}`,
          x: 0, // Will be positioned when document renders
          y: 0,
          text: commentJson.commentText || '',
          author: commentAuthorFromUser(commentJson.author, item.userid),
          timestamp: new Date(item.created_at || item.updated_at),
          selectedText: commentJson.selectedText || '',
          paragraphIndex: commentJson.paragraphIndex,
          startOffset: startOffset, // Store startOffset for positioning
          endOffset: endOffset, // Store endOffset for positioning
          beforeText: paragraphText.substring(0, startOffset) || '',
          afterText: paragraphText.substring(endOffset) || '',
          contextText: paragraphText || '',
          docxCommentId: `api-comment-${item.id}`,
          fileName: commentJson.fileName || fileName || undefined, // Store fileName from API
          initials: commentJson.initials || undefined, // Store initials from API
        };
      });

      console.log('✅ Converted comments:', convertedComments);
      setComments(convertedComments);
    } catch (error) {
      console.error('❌ Failed to load comments from API:', error);
      setComments([]);
    }
  }, [fileId, docxBlob, fileName]);

  // Load comments from API when fileId and docxBlob are available
  useEffect(() => {
    loadCommentsFromAPI();
  }, [loadCommentsFromAPI]);

  const handleClose = () => {
    // Ensure MyFacilityDetails restores Mock Survey tab/accordion
    // even when history state is not preserved.
    sessionStorage.setItem('openMockSurveyOnReturn', '1');

    // Optionally clear localStorage when navigating away (comment out if you want to keep the file)
    // localStorage.removeItem('surveyViewerFile');
    // localStorage.removeItem('surveyViewerFileData');
    
    // Navigate back to spaces page with mock survey tab active and accordion open
    navigate('/dashboard/facilities', {
      state: {
        currentView: 'MyFacility',
        activeTab: 'surveyorvisit',
        mockSurveyAccordionOpen: true,
      },
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if user has add permission for Mock Survey (by feature name)
    if (!canAddByName(FEATURE_NAMES.MOCK_SURVEY)) {
      toast.error('You do not have permission to upload documents');
      return;
    }

    // Validate file type
    if (!file.name.match(/\.(docx?|doc)$/i)) {
      setError('Please upload a valid DOCX or DOC file.');
      return;
    }

    setLoading(true);
    setError(null);
    setFileName(file.name);
    setComments([]); // Reset comments for new file

    try {
      // Get facility ID from sessionStorage
      const facilityData = sessionStorage.getItem('filtered_selectedFacility');
      let facility = 'test'; // Default fallback
      
      if (facilityData) {
        try {
          const parsed = JSON.parse(facilityData);
          // Prioritize facility_id, convert to string
          const facilityId = parsed.facility_id || parsed.facilityId;
          if (facilityId !== undefined && facilityId !== null) {
            facility = String(facilityId);
          } else if (parsed.facility_name) {
            facility = parsed.facility_name;
          }
        } catch (e) {
          console.warn('Could not parse facility data, using default');
        }
      }

      // Upload file to backend automatically
      toast.info('Uploading and saving file...');
      const uploadResult = await mockSurveyService.uploadFile(file, facility);
      
      if (uploadResult.success || uploadResult.file_id) {
        // Set the file ID from upload result
        const newFileId = uploadResult.file_id;
        setFileId(newFileId);
        
        // Read the file as base64 for localStorage and as ArrayBuffer for blob
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const arrayBuffer = e.target?.result as ArrayBuffer;
            if (arrayBuffer) {
              const blob = new Blob([arrayBuffer], {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              });
              setDocxBlob(blob);
              
              // Convert arrayBuffer to base64 for localStorage
              const bytes = new Uint8Array(arrayBuffer);
              let binary = '';
              for (let i = 0; i < bytes.length; i++) {
                binary += String.fromCharCode(bytes[i]);
              }
              const base64Data = btoa(binary);
              
              // Save to localStorage for persistence across refreshes (include fileId)
              localStorage.setItem('surveyViewerFile', JSON.stringify({
                fileName: file.name,
                fileType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                fileId: newFileId,
              }));
              localStorage.setItem('surveyViewerFileData', base64Data);
              
              setLoading(false);
              toast.success('File uploaded and saved successfully!');
              void logSurveyViewerAuditEvent(
                'UPLOAD',
                '/api/v1/mock-survey/mock-survey-file-upload',
                'POST',
                {
                  action: 'UPLOAD',
                  source: 'Survey Viewer',
                  fileName: file.name,
                  fileId: newFileId,
                },
              );
            }
          } catch (err) {
            console.error('Failed to load uploaded document', err);
            setError('Failed to load the uploaded document. Please try again.');
            setLoading(false);
          }
        };

        reader.onerror = () => {
          setError('Failed to read the uploaded file. Please try again.');
          setLoading(false);
        };

        reader.readAsArrayBuffer(file);
      } else {
        setError(uploadResult.message || 'Failed to save file to backend');
        setLoading(false);
        toast.error(uploadResult.message || 'Failed to save file to backend');
        void logSurveyViewerAuditEvent(
          'UPLOAD',
          '/api/v1/mock-survey/mock-survey-file-upload',
          'POST',
          {
            action: 'UPLOAD',
            source: 'Survey Viewer',
            fileName: file.name,
          },
        'SURVEY_VIEWER',
          500,
          uploadResult.message || 'Failed to save file to backend',
        );
      }
    } catch (error: any) {
      console.error('Failed to upload file:', error);
      setError(error.message || 'Failed to upload file. Please try again.');
      setLoading(false);
      toast.error(error.message || 'Failed to upload file. Please try again.');
      void logSurveyViewerAuditEvent(
        'UPLOAD',
        '/api/v1/mock-survey/mock-survey-file-upload',
        'POST',
        {
          action: 'UPLOAD',
          source: 'Survey Viewer',
          fileName: file.name,
        },
        'SURVEY_VIEWER',
        500,
        error.message || 'Failed to upload file. Please try again.',
      );
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDownload = async () => {
    if (!canUpdateMockSurvey) {
      toast.error('You do not have permission to download this document');
      return;
    }

    if (!fileId) {
      console.error('❌ No fileId available for download');
      setError('Cannot download: File ID not found. Please ensure the file is saved to the backend.');
      return;
    }

    try {
      setDownloading(true); // Use downloading state instead of loading
      setError(null); // Clear any previous errors
      console.log('📥 Downloading fresh file from backend, fileId:', fileId);
      console.log('📥 API endpoint: /api/v1/mock-survey/mock-survey-file-get?fileid=' + fileId);
      
      // Download fresh file from backend (backend embeds comments)
      const blob = await mockSurveyService.downloadFileWithComments(fileId, fileName);
      
      // Create download link with USA date/time format: filename_MMDDYYYYHHMM.ext
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Format filename with USA date/time: filename_MMDDYYYYHHMM.ext
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");
      const month = pad(now.getMonth() + 1);
      const day = pad(now.getDate());
      const year = now.getFullYear();
      const hours = pad(now.getHours());
      const minutes = pad(now.getMinutes());
      const timestamp = `${month}${day}${year}${hours}${minutes}`;
      
      // Extract base name and extension
      const lastDotIndex = fileName.lastIndexOf(".");
      const baseName = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : (fileName || 'document');
      const extension = lastDotIndex > 0 ? fileName.substring(lastDotIndex + 1) : 'docx';
      
      a.download = `${baseName}_${timestamp}.${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setDownloading(false); // Use downloading state instead of loading
      console.log('✅ File downloaded successfully from backend');
      void logSurveyViewerAuditEvent(
        'DOWNLOAD',
        '/api/v1/mock-survey/mock-survey-file-get',
        'GET',
        {
          action: 'DOWNLOAD',
          fileId,
          fileName,
        },
      );
    } catch (error: any) {
      console.error('❌ Failed to download file from backend:', error);
      setError(error.message || 'Failed to download file. Please try again.');
      setDownloading(false); // Use downloading state instead of loading
      void logSurveyViewerAuditEvent(
        'DOWNLOAD',
        '/api/v1/mock-survey/mock-survey-file-get',
        'GET',
        {
          action: 'DOWNLOAD',
          fileId,
          fileName,
        },
        'SURVEY_VIEWER',
        500,
        error.message || 'Failed to download file. Please try again.',
      );
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        minHeight="400px"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress />
        <Typography>Loading document...</Typography>
      </Box>
    );
  }

  if (error && !docxBlob) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        minHeight="400px"
        flexDirection="column"
        gap={2}
        sx={{ padding: 4 }}
      >
        <Typography color="error" variant="h6" gutterBottom>
          {error}
        </Typography>
        {canAddByName(FEATURE_NAMES.MOCK_SURVEY) && (
          <>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={handleUploadClick}
              sx={{ mt: 2 }}
            >
              Upload Document
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".doc,.docx"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {docxBlob ? (
        <>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f5f5f5',
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton
                onClick={handleClose}
                sx={{
                  color: '#065B70',
                  '&:hover': {
                    backgroundColor: '#EAF8FB',
                  },
                }}
                title="Back to Mock Survey"
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="body2" color="text.secondary">
                Current Document: <strong>{fileName}</strong>
              </Typography>
            </Box>
            {canAddByName(FEATURE_NAMES.MOCK_SURVEY) && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CloudUpload />}
                  onClick={handleUploadClick}
                  sx={{
                    borderColor: '#5BBFD6',
                    color: '#065B70',
                    '&:hover': {
                      backgroundColor: '#EAF8FB',
                      borderColor: '#5BBFD6',
                    },
                  }}
                >
                  Upload New Document
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".doc,.docx"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </>
            )}
          </Paper>
          {error && (
            <Box sx={{ p: 2, backgroundColor: '#ffebee' }}>
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            </Box>
          )}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <DocumentViewer 
              key={`${fileName}-${docxBlob?.size || 0}`} // Force remount when file changes
              docxBlob={docxBlob} 
              onDownloadFromBackend={handleDownload} // Use backend download (backend embeds comments)
              originalBlob={docxBlob}
              fileName={fileName}
              fileId={fileId}
              downloading={downloading} // Pass downloading state to show button loader
              initialComments={comments} // Pass loaded comments from API
              readOnly={viewerReadOnly}
              onSaveComment={async (commentData) => {
                if (fileId) {
                  try {
                    await mockSurveyService.addComment(fileId, {
                      ...commentData,
                      file_name: fileName || 'document.docx', // Ensure file_name is included
                    });
                    void logSurveyViewerAuditEvent(
                      'CREATE',
                      '/api/v1/mock-survey/mock-survey-file-add-comment',
                      'POST',
                      {
                        action: 'CREATE_COMMENT',
                        fileId,
                        fileName,
                        paragraphIndex: commentData.paragraphIndex,
                        selectedText: commentData.selectedText,
                      },
                    'MOCK_SURVEY',
                    );
                  } catch (error: any) {
                    void logSurveyViewerAuditEvent(
                      'CREATE',
                      '/api/v1/mock-survey/mock-survey-file-add-comment',
                      'POST',
                      {
                        action: 'CREATE_COMMENT',
                        fileId,
                        fileName,
                        paragraphIndex: commentData.paragraphIndex,
                      },
                      'MOCK_SURVEY',
                      500,
                      error?.message || 'Failed to add comment',
                    );
                    throw error;
                  }
                  // Reload comments after saving
                  try {
                    const response = await mockSurveyService.getComments(fileId);
                    const convertedComments: CommentData[] = response.items.map((item) => {
                      const commentJson = item.commentsjson;
                      const paragraphText = commentJson.paragraphText || '';
                      const startOffset = commentJson.startOffset || 0;
                      const endOffset = commentJson.endOffset || 0;
                      
                      return {
                        id: `api-comment-${item.id}`,
                        x: 0,
                        y: 0,
                        text: commentJson.commentText || '',
                        author: commentAuthorFromUser(commentJson.author, item.userid),
                        timestamp: new Date(item.created_at || item.updated_at),
                        selectedText: commentJson.selectedText || '',
                        paragraphIndex: commentJson.paragraphIndex,
                        startOffset: startOffset,
                        endOffset: endOffset,
                        beforeText: paragraphText.substring(0, startOffset) || '',
                        afterText: paragraphText.substring(endOffset) || '',
                        contextText: paragraphText || '',
                        docxCommentId: `api-comment-${item.id}`,
                        fileName: commentJson.fileName || fileName || undefined, // Store fileName from API
                        initials: commentJson.initials || undefined, // Store initials from API
                      };
                    });
                    setComments(convertedComments);
                  } catch (error) {
                    console.error('Failed to reload comments after save:', error);
                  }
                }
              }}
              onCommentDeleted={async (deleted) => {
                await loadCommentsFromAPI();
                void logSurveyViewerAuditEvent(
                  'DELETE',
                  '/api/v1/mock-survey/mock-survey-file-remove-comment',
                  'POST',
                  {
                    action: 'DELETE_COMMENT',
                    fileId,
                    fileName,
                    commentId: deleted?.commentId,
                    paragraphIndex: deleted?.paragraphIndex,
                  },
                  'MOCK_SURVEY',
                );
              }}
              onCommentUpdated={async (commentId, updates) => {
                // Extract numeric ID from commentId (format: 'api-comment-{id}')
                const numericId = parseInt(commentId.replace('api-comment-', ''), 10);
                if (!fileId || isNaN(numericId)) {
                  throw new Error('Invalid comment ID or file ID');
                }

                console.log('📝 Updating comment with full payload:', {
                  id: numericId,
                  fileid: typeof fileId === 'string' ? parseInt(fileId, 10) : fileId,
                  ...updates
                });

                try {
                  await mockSurveyService.updateComment({
                    id: numericId,
                    fileid: typeof fileId === 'string' ? parseInt(fileId, 10) : fileId,
                    commentText: updates.text,
                    author: updates.author,
                    initials: updates.initials,
                    fileName: updates.fileName,
                    paragraphIndex: updates.paragraphIndex,
                    startOffset: updates.startOffset,
                    endOffset: updates.endOffset,
                    selectedText: updates.selectedText,
                  });
                  void logSurveyViewerAuditEvent(
                    'UPDATE',
                    '/api/v1/mock-survey/mock-survey-file-comments/update',
                    'PUT',
                    {
                      action: 'UPDATE_COMMENT',
                      fileId,
                      fileName,
                      commentId: numericId,
                      paragraphIndex: updates.paragraphIndex,
                    },
                    'MOCK_SURVEY',
                  );
                } catch (error: any) {
                  void logSurveyViewerAuditEvent(
                    'UPDATE',
                    '/api/v1/mock-survey/mock-survey-file-comments/update',
                    'PUT',
                    {
                      action: 'UPDATE_COMMENT',
                      fileId,
                      fileName,
                      commentId: numericId,
                    },
                    'MOCK_SURVEY',
                    500,
                    error?.message || 'Failed to update comment',
                  );
                  throw error;
                }

                // Reload comments after updating
                try {
                  await loadCommentsFromAPI();
                } catch (error) {
                  console.error('Failed to reload comments after update:', error);
                }
              }}
            />
          </Box>
        </>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
          minHeight="400px"
          flexDirection="column"
          gap={2}
          sx={{ padding: 4 }}
        >
          <Paper
            elevation={2}
            sx={{
              p: 2,
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f5f5f5',
              mb: 2,
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton
                onClick={handleClose}
                sx={{
                  color: '#065B70',
                  '&:hover': {
                    backgroundColor: '#EAF8FB',
                  },
                }}
                title="Back to Mock Survey"
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h6" color="text.primary">
                Survey Viewer
              </Typography>
            </Box>
          </Paper>
          <Box
            sx={{
              width: '100%',
              maxWidth: '600px',
              border: '1px solid #99D4E1',
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              padding: 3,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 3,
                color: '#737373',
                fontSize: '14px',
                lineHeight: 1.6,
              }}
            >
              We offer a full policy review to ensure current compliance standards based on your facility's regulations, surveyor feedback and expertise.
            </Typography>
            
            {canAddByName(FEATURE_NAMES.MOCK_SURVEY) ? (
              <Box
                component="label"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed #a3a3a3',
                  borderRadius: '8px',
                  padding: 4,
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: '#5BBFD6',
                    backgroundColor: '#f0fdff',
                  },
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".doc,.docx"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <Button
                  variant="contained"
                  startIcon={<CloudUpload />}
                  onClick={(e) => {
                    e.preventDefault();
                    handleUploadClick();
                  }}
                  sx={{
                    backgroundColor: '#E6FAFC',
                    color: '#065B70',
                    border: '1px solid #5BBFD6',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    textTransform: 'none',
                    fontSize: '16px',
                    fontWeight: 400,
                    fontFamily: 'Rethink Sans, sans-serif',
                    '&:hover': {
                      backgroundColor: '#D1F5F8',
                    },
                  }}
                >
                  Upload Documents
                </Button>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 2,
                    color: '#737373',
                    fontSize: '12px',
                  }}
                >
                  Supported file type: DOC, DOCX
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: '#737373',
                  fontSize: '14px',
                  textAlign: 'center',
                  padding: 2,
                }}
              >
                You do not have permission to upload documents.
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SurveyViewer;

